import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../../lib/Icon";
import { ANNOTATION_COLORS } from "@lancer-vtt/domain";
import type { Annotation, EncounterCommand } from "@lancer-vtt/domain";
import type { EncounterDispatchResult } from "../../../lib/encounter/remoteEncounterController";
import { recolorAnnotationCommand, removeAnnotationCommand } from "../combatDrawing";
import type { View } from "./types";

/**
 * The select gesture's tiny inspector: a small HTML panel anchored
 * over the double-clicked stroke's first point, offering recolor (the four {@link ANNOTATION_COLORS}
 * swatches — reuses the tool rail strip's `combat-draw-swatch-*` classes so a color reads identically
 * in both places) + delete. **Ownership is an AFFORDANCE here, never the wall**: `owned` (the actor's
 * own stroke, or the GM) decides whether these controls even RENDER; ownership is never re-checked
 * client-side beyond that (recolor/delete dispatch unconditionally once shown) — the real enforcement
 * is the server gate (`canIssueEncounter`), proven independently by the
 * cross-owner-erase e2e proof and `cli/proofs/annotations.test.ts`. A non-owner/non-GM viewer sees a
 * read-only "by {authorSlotId}" label instead — informative, not interactive.
 */
export function AnnotationInspector({
  annotation,
  view,
  isGm,
  actorId,
  dispatch,
  slotNameOf,
  onClose,
}: {
  annotation: Annotation;
  view: View;
  isGm: boolean;
  actorId: string;
  dispatch: (command: EncounterCommand) => EncounterDispatchResult;
  slotNameOf: (slotId: string) => string;
  onClose: () => void;
}) {
  const owned = isGm || annotation.authorSlotId === actorId;
  const anchor = annotation.points[0] ?? { x: 0, y: 0 };
  const left = view.x + anchor.x * view.scale;
  const top = view.y + anchor.y * view.scale;
  return (
    <div className="combat-annot-inspector" style={{ left, top }} role="group" aria-label="annotation inspector">
      {owned ? (
        <>
          <div className="combat-annot-inspector-swatches" role="group" aria-label="recolor stroke">
            {ANNOTATION_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`combat-draw-swatch combat-draw-swatch-${c}${annotation.color === c ? " combat-draw-swatch-on" : ""}`}
                aria-pressed={annotation.color === c}
                aria-label={`recolor ${c}`}
                title={c}
                onClick={() => {
                  if (annotation.color !== c) dispatch(recolorAnnotationCommand({ actorId, annotation, color: c }));
                }}
              />
            ))}
          </div>
          <button
            type="button"
            className="combat-annot-inspector-delete"
            aria-label="delete annotation"
            onClick={() => {
              dispatch(removeAnnotationCommand({ actorId, annotationId: annotation.id }));
              onClose();
            }}
          >
            <Icon icon={faTrashCan} className="icon-lead" />Delete
          </button>
        </>
      ) : (
        // `authorSlotId === null` means GM-authored (see the `Annotation` type doc) — resolve it via
        // the real "gm" slot rather than a hardcoded "GM" literal, so it stays in lockstep with
        // whatever `useSlotNames` actually resolves that slot to.
        <div className="combat-annot-inspector-readonly">by {slotNameOf(annotation.authorSlotId ?? "gm")}</div>
      )}
    </div>
  );
}
